import appService from '../../server/app.service';
import _ from 'lodash';
import { getField, updateField } from 'vuex-map-fields';

function normalizeTags(tagIds) {
    if (!tagIds) return [];
    if (Array.isArray(tagIds)) {
        // Nuovo formato: array di oggetti con 'channel'
        if (tagIds.length === 0 || tagIds[0].channel !== undefined) return tagIds;
        return [];
    }
    // Vecchio formato: {"gtm": ["GTM-XXX"], "bing": ["BING-YYY"]}
    const tags = [];
    for (const [channel, ids] of Object.entries(tagIds)) {
        if (Array.isArray(ids)) {
            ids.forEach(id => { if (id) tags.push({ channel, id, type_id: null }); });
        }
    }
    return tags;
}

const state = {
    projects: [],
    project: [],
    sites: [],
    bookings: [],
    scriptTypes: [],
    libraries: [],
    scripts: [],
    languages: [],
    languageSelected: 'ita',
    translations: [],
    id: 0,
    collapsedScriptTypes: [],
};

const getters = {
    collapsedScriptTypes: (state) => state.collapsedScriptTypes,
    projects: (state) => state.projects,
    project: (state) => state.project,
    sites: (state) => state.sites,
    bookings: (state) => state.bookings,
    scriptTypes: (state) => state.scriptTypes,
    libraries: (state) => state.libraries,
    scripts: (state) => state.scripts,
    languages: (state) => state.languages,
    languageSelected: (state) => state.languageSelected,
    translations: (state) => state.translations,
    getField,
};

const actions = {
    async loadProjects({ commit, dispatch }, credentials) {
        try {
            const data = await appService.connectToServer(credentials);
            commit('loadProjects', { projects: data });
        } catch (err) {
            dispatch('addMessage', err.toString(), { root: true });
        }
    },
    async loadProject({ commit, dispatch }, credentials) {
        try {
            const data = await appService.connectToServer(credentials);
            commit('loadProject', { project: data });
        } catch (err) {
            dispatch('addMessage', err.toString(), { root: true });
        }
    },
    async loadChannels({ commit, dispatch }, credentials) {
        try {
            const data = await appService.connectToServer(credentials);
            commit('loadChannels', { project: data });
        } catch (err) {
            dispatch('addMessage', err.toString(), { root: true });
        }
    },
    async createProject({ commit, dispatch }, credentials) {
        try {
            const data = await appService.connectToServer(credentials);
            commit('createProject', { project: data });
        } catch (err) {
            dispatch('addMessage', err.toString(), { root: true });
        }
    },
    async saveProject({ commit, dispatch }, credentials) {
        try {
            const data = await appService.connectToServer(credentials);
            if (data.success) {
                commit('saveProjectSuccess', {
                    Project: data.Project,
                    Scripts: data.Scripts,
                    ProjectSite: data.ProjectSite,
                    ProjectBooking: data.ProjectBooking,
                });
                dispatch('addMessage', data, { root: true });
                return data.Project;
            }
            dispatch('addMessage', data, { root: true });
        } catch (err) {
            dispatch('addMessage', err.toString(), { root: true });
        }
    },
    async deleteProject({ commit, dispatch }, credentials) {
        try {
            const data = await appService.connectToServer(credentials);
            if (data.success) {
                commit('deleteProject', { id: data.id });
                dispatch('addMessage', data, { root: true });
            } else {
                dispatch('addMessage', data, { root: true });
            }
        } catch (err) {
            dispatch('addMessage', err.toString(), { root: true });
        }
    },

    addSite({ commit }) {
        commit('addSite');
    },
    addBooking({ commit }) {
        commit('addBooking');
    },
    addScript({ commit }) {
        commit('addScript');
    },
    deleteSite({ commit }, id) {
        commit('deleteSite', id);
    },
    deleteBooking({ commit }, id) {
        commit('deleteBooking', id);
    },
    deleteScript({ commit }, id) {
        commit('deleteScript', id);
    },
    languageSelect({ commit }, slug) {
        commit('languageSelect', slug);
    },
    resetProject({ commit }) {
        commit('resetProject');
    },
};

const mutations = {
    toggleCollapsedScriptType(state, id) {
        const i = state.collapsedScriptTypes.indexOf(id);
        if (i > -1) state.collapsedScriptTypes.splice(i, 1);
        else state.collapsedScriptTypes.push(id);
    },
    setCollapsedScriptTypes(state, ids) {
        state.collapsedScriptTypes = ids;
    },
    loadProjects(state, { projects }) {
        state.projects = projects.result;
    },
    loadProject(state, { project }) {
        state.project = project.result;
        state.sites = project.data.ProjectSite.map(s => {
            var rawTags = s.tags != null ? s.tags : normalizeTags(s.tag_ids);
            var gtmTag = rawTags.find(t => t.channel === 'gtm') || { channel: 'gtm', id: '', type_id: null };
            var otherTags = rawTags.filter(t => t.channel !== 'gtm');
            return { ...s, tags: [gtmTag].concat(otherTags) };
        });
        state.bookings = project.data.ProjectBooking.map(b => {
            var rawTags = b.tags != null ? b.tags : normalizeTags(b.tag_ids);
            var gtmTag = rawTags.find(t => t.channel === 'gtm') || { channel: 'gtm', id: '', type_id: null };
            var otherTags = rawTags.filter(t => t.channel !== 'gtm');
            return { ...b, tags: [gtmTag].concat(otherTags) };
        });
        state.scriptTypes = project.data.ScriptType;
        state.libraries = project.data.ProjectLibraries;
        state.scripts = project.data.Scripts;
        state.languages = project.data.languages;
        state.translations = project.data.translations;
    },
    loadChannels(state, { project }) {
        state.project = project.result;
        state.bookings = project.data.ProjectBooking;
    },
    createProject(state, { project }) {
        state.scriptTypes = project.data.ScriptType;
        state.languages = project.data.languages;
        state.translations = project.data.translations;
    },
    saveProjectSuccess(state, { Project, Scripts, ProjectSite, ProjectBooking }) {
        state.project.requestLogCreate = Project.requestLogCreate;
        state.project.requestLogUpade = Project.requestLogUpade;
        state.project.requestLogOfflineConversion = Project.requestLogOfflineConversion;
        state.scripts = Scripts;
        state.sites = ProjectSite.map(s => {
            var rawTags = s.tags != null ? s.tags : normalizeTags(s.tag_ids);
            var gtmTag = rawTags.find(t => t.channel === 'gtm') || { channel: 'gtm', id: '', type_id: null };
            var otherTags = rawTags.filter(t => t.channel !== 'gtm');
            return { ...s, tags: [gtmTag].concat(otherTags) };
        });
        state.bookings = ProjectBooking.map(b => {
            var rawTags = b.tags != null ? b.tags : normalizeTags(b.tag_ids);
            var gtmTag = rawTags.find(t => t.channel === 'gtm') || { channel: 'gtm', id: '', type_id: null };
            var otherTags = rawTags.filter(t => t.channel !== 'gtm');
            return { ...b, tags: [gtmTag].concat(otherTags) };
        });
    },
    deleteProject(state, { id }) {
        const idx = _.findIndex(state.projects, (o) => o.id === id);
        if (idx > -1) state.projects.splice(idx, 1);
    },
    addSite(state) {
        state.sites.push({ active: '1', site: '', name: '', folder: '', idBlastbook:'', accommodationID:'', tags: [{ channel: 'gtm', id: '', type_id: null }], id: '', deleted: false });
    },
    addBooking(state) {
        state.bookings.push({ type: 'id_albergo', active: '1', booking: '', id: '', idBlastbook:'', name: '', accommodationID:'', tags: [{ channel: 'gtm', id: '', type_id: null }], custom_domain:'', deleted: false });
    },
    addScript(state) {
        state.scripts.push({ active: '1', platform: '1', type_id: '1', script: '', id: '', deleted: false });
    },
    addSiteTag(state, siteIndex) {
        if (state.sites[siteIndex]) {
            if (!Array.isArray(state.sites[siteIndex].tags)) {
                state.sites[siteIndex].tags = [];
            }
            state.sites[siteIndex].tags.push({ channel: 'gtm', id: '', type_id: null });
        }
    },
    removeSiteTag(state, { siteIndex, tagIndex }) {
        if (state.sites[siteIndex] && Array.isArray(state.sites[siteIndex].tags)) {
            state.sites[siteIndex].tags.splice(tagIndex, 1);
        }
    },
    addBookingTag(state, bookingIndex) {
        if (state.bookings[bookingIndex]) {
            if (!Array.isArray(state.bookings[bookingIndex].tags)) {
                state.bookings[bookingIndex].tags = [];
            }
            state.bookings[bookingIndex].tags.push({ channel: 'gtm', id: '', type_id: null });
        }
    },
    removeBookingTag(state, { bookingIndex, tagIndex }) {
        if (state.bookings[bookingIndex] && Array.isArray(state.bookings[bookingIndex].tags)) {
            state.bookings[bookingIndex].tags.splice(tagIndex, 1);
        }
    },
    deleteSite(state, id) {
        if (state.sites[id]) state.sites[id].deleted = true;
    },
    deleteBooking(state, id) {
        if (state.bookings[id]) state.bookings[id].deleted = true;
    },
    deleteScript(state, id) {
        if (state.scripts[id]) state.scripts[id].deleted = true;
    },
    languageSelect(state, slug) {
        state.languageSelected = slug.slug;
    },
    resetProject(state) {
        state.collapsedScriptTypes = [];
        state.project = [];
        state.sites = [];
        state.bookings = [];
        state.scriptTypes = [];
        state.libraries = [];
        state.scripts = [];
        state.languageSelected = 'ita';
        state.translations = [];
    },
    updateScriptsOrder(state, newOrder) {
        state.scripts = [...newOrder];
    },
    updateField,
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
